import type { Session } from './types'
import { researchContractFor } from './researchContract'
import { defaultExperimentReview, experimentReviewPackage } from './experimentReview'
import {
  ASSESSMENT_LIMITS,
  assessmentBytes,
  assessmentFingerprint,
  assessmentInputRevision,
  assessmentNodeIds,
  parseAssessmentPacket,
  type AssessmentAttachment,
  type AssessmentMode,
  type AssessmentPacket,
  type AssessmentReference,
} from './runAssessment'

/** This packet is shared by the UI, controller and paired evaluation. No
 * clipping, rating-based selection, invented citations or private evaluator data. */
export function buildAssessmentPacket(
  session: Session,
  runId: string,
  mode: AssessmentMode = 'research',
  attachments: AssessmentAttachment[] = [],
  focusNodeId?: string,
): AssessmentPacket {
  const run = session.runs.find((r) => r.id === runId)
  if (!run?.finishedAt) throw new Error('Let the run finish before reviewing its results.')
  const allNodeIds = assessmentNodeIds(session, runId)
  if (focusNodeId && !allNodeIds.includes(focusNodeId))
    throw new Error('Select a retained claim from this run or its premises.')
  const selectedIds = assessmentNodeIds(session, runId, focusNodeId)
  if (!selectedIds.length) throw new Error('This run has no retained claims to assess.')
  if (attachments.length > ASSESSMENT_LIMITS.attachments)
    throw new Error(`Select at most ${ASSESSMENT_LIMITS.attachments} attachments.`)
  const contract = researchContractFor(
    session,
    'research',
    selectedIds,
    mode === 'originality'
      ? 'Compare exact definitions, inference rules, encodings and theorem hypotheses against retained prior work. Missing search matches leave originality unresolved.'
      : 'Identify the strongest supported contribution, distinguish correctness, originality and usefulness, preserve negative findings and propose a discriminating next question.',
  )
  const nodeIds = [...new Set([...selectedIds, ...contract.definitions.map((d) => d.id)])]
  const references: AssessmentReference[] = []
  const add = (
    id: string,
    label: string,
    kind: AssessmentReference['kind'],
    provenance: string,
    value: unknown,
  ) => {
    const content = typeof value === 'string' ? value : JSON.stringify(value)
    references.push({
      id,
      label,
      kind,
      provenance,
      content,
      revision: assessmentFingerprint(content),
    })
  }
  for (const nodeId of nodeIds) {
    const n = session.nodes[nodeId]!
    n.verification?.sources.forEach((source, index) =>
      add(
        `source:${nodeId}:${index}`,
        source.title || source.url,
        'source',
        'Retained searched source; its own opening and quote-check fields describe the available evidence.',
        source,
      ),
    )
    if (n.outcome)
      add(
        `outcome:${nodeId}`,
        `Recorded outcome on ${n.label}`,
        'evidence',
        'A recorded observation and its declared interpretation, not authenticated reviewer identity.',
        n.outcome,
      )
  }
  for (const theorem of contract.mathematics?.workspace.theorems ?? [])
    add(
      `theorem:${theorem.id}:${theorem.revision}`,
      theorem.title,
      'theorem',
      'Recorded theorem with source locator and hypotheses; a manual source locator is not an admitted searched citation.',
      theorem,
    )
  for (const obligation of contract.mathematics?.workspace.obligations ?? [])
    add(
      `obligation:${obligation.id}:${obligation.revision}`,
      obligation.title,
      'obligation',
      'Revision-bound mathematical obligation; execution completion cannot discharge it.',
      obligation,
    )
  for (const evidence of contract.mathematics?.workspace.evidence ?? [])
    add(
      `mathematical-evidence:${evidence.id}`,
      evidence.kind,
      'evidence',
      'Recorded mathematical evidence. Exact execution and its interpretation have separate obligations.',
      evidence,
    )
  for (const binding of session.research?.manualEvidenceBindings ?? []) {
    if (!nodeIds.includes(binding.nodeId)) continue
    const record = session.research?.evidenceRecords.find((r) => r.id === binding.recordId)
    if (record && !references.some((r) => r.id === `evidence:${record.id}`))
      add(
        `evidence:${record.id}`,
        record.label,
        'evidence',
        `Recorded ${record.origin}; source admission is separate.`,
        record,
      )
  }
  for (const receipt of session.research?.experiments?.receipts ?? []) {
    if (!receipt.spec.context.nodeId || !nodeIds.includes(receipt.spec.context.nodeId)) continue
    add(
      `experiment:${receipt.id}`,
      receipt.spec.title,
      'experiment',
      'Executed input and public results with exact checker identity; finite execution is not a universal theorem.',
      experimentReviewPackage(receipt, defaultExperimentReview(receipt)),
    )
  }
  for (const attachment of attachments) {
    if (
      !attachment.id ||
      !attachment.title ||
      !attachment.content ||
      !['argument', 'checker', 'certificate', 'source-text', 'observation'].includes(
        attachment.kind,
      ) ||
      !['user-supplied', 'ai-authored', 'local-execution'].includes(attachment.provenance) ||
      new TextEncoder().encode(attachment.content).length > ASSESSMENT_LIMITS.attachmentBytes
    )
      throw new Error(
        `Attachment ${attachment.title || '(untitled)'} is invalid or exceeds ${ASSESSMENT_LIMITS.attachmentBytes} bytes.`,
      )
    add(
      `attachment:${attachment.id}`,
      attachment.title,
      'attachment',
      `${attachment.provenance} ${attachment.kind}; supplied text, not proof certification or a searched citation.`,
      attachment.content,
    )
    references[references.length - 1]!.attachmentKind = attachment.kind
  }
  const body = {
    version: 1 as const,
    sessionId: session.id,
    runId,
    mode,
    inputRevision: assessmentInputRevision(session, runId),
    context: JSON.stringify(contract),
    nodeIds,
    ...(focusNodeId ? { focusNodeId } : {}),
    references,
    missingContext: [
      ...contract.missingContext,
      ...(focusNodeId
        ? [
            `Focused review of ${session.nodes[focusNodeId]!.label} and its supplied dependencies; other run branches are outside this assessment.`,
          ]
        : []),
      ...(!references.some((r) => r.kind === 'source' || r.kind === 'theorem')
        ? [
            'No retained searched source or recorded theorem is available for a structural prior-work comparison.',
          ]
        : []),
    ],
  }
  const packet = { ...body, revision: assessmentFingerprint(body) }
  if (assessmentBytes(packet) > ASSESSMENT_LIMITS.packetBytes)
    throw new Error(
      `The complete dossier exceeds ${ASSESSMENT_LIMITS.packetBytes} bytes. Export a focused continuation with its premises; no definitions were clipped.`,
    )
  const checked = parseAssessmentPacket(packet)
  if (!checked) throw new Error('The dossier contains invalid or duplicated references.')
  return checked
}
